import {
  AlertTriangle,
  Bell,
  CalendarClock,
  Check,
  Copy,
  Eye,
  LayoutTemplate,
  Loader2,
  Mail,
  MoreHorizontal,
  Palette,
  Send,
  Sparkles,
  Trash2,
  Video
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FC
} from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeLintIssue
} from "@/api/types/outgoingIntake";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { Textarea } from "@/components/ui/textarea";
import { useAgentPanelStore } from "@/features/agents/agentPanelStore";
import {
  estimateLine,
  intakeEstimate
} from "@/features/foreignNationalOutgoingIntakes/intakeEstimate";
import {
  ChecksList,
  insertInSection,
  LibraryPicker,
  PageBar,
  QuestionsCard,
  RailLine,
  RailSection,
  SectionJumpList,
  useBuilderSelection
} from "@/features/intakeBuilder";
import { cn } from "@/lib/utils";

import { useIsBuilding } from "../buildingDrafts";
import { useRequestsIde } from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";
import { errorCount, presendChecklist } from "../editor/presend";
import { cadenceLabel } from "../editor/reminders";
import { SendSheet } from "../editor/SendSheet";
import { addNodes, findNode, stepsOf, updateNode } from "../editor/stepModel";
import {
  isEmail,
  useIntakeDraft,
  type IntakeDraft,
  type SaveState
} from "../editor/useIntakeDraft";
import { firstName } from "../editor/useSubmissionReview";
import { LibraryDialog } from "../editor/welcomeVideo/LibraryDialog";
import { RecordWelcomeVideoDialog } from "../editor/welcomeVideo/RecordWelcomeVideoDialog";
import {
  useIntakeWelcomeVideo,
  type IntakeWelcomeVideo
} from "../editor/welcomeVideo/useIntakeWelcomeVideo";
import { ASSISTANT_NAME, intakeArtifact, previewArtifact } from "../model";
import { useIntakeActions } from "../useIntakeActions";
import { DeliverySheet } from "./DeliverySheet";
import { PreviewDialog } from "./PreviewDialog";
import { TrackPage } from "./TrackPage";
import { useRequestsLibrary } from "./useRequestsLibrary";

type Props = {
  intakeId: string;
  clientEmail: string | null;
  clientName: string | null;
  /** `?open=preview:<id>`: the client's view over the page. */
  preview?: boolean;
};

const NOTE_ID = "intake-note";

const DraftSaveState: FC<{ state: SaveState; onRetry: () => void }> = ({
  state,
  onRetry
}) => {
  if (state === "error")
    return (
      <Button
        variant="link"
        size="xs"
        className="text-danger"
        onClick={onRetry}
      >
        Not Saved · Retry
      </Button>
    );
  return (
    <span
      aria-live="polite"
      className="inline-flex items-center gap-1 text-sm whitespace-nowrap text-fg-3"
    >
      {state === "saved" ? (
        <>
          <Check className="size-3" /> Saved
        </>
      ) : (
        <>
          <Loader2 className="size-3 animate-spin" /> Saving
        </>
      )}
    </span>
  );
};

type Fact = {
  icon: ComponentType<{ className?: string }>;
  text: string;
  tone?: "default" | "muted" | "danger";
};

/** The delivery settings as short facts, for the rail and the bar. */
const deliveryFacts = (
  draft: IntakeDraft,
  video: IntakeWelcomeVideo
): Fact[] => {
  const { settings } = draft;
  const to = settings.recipient_email.trim();
  const reminders = cadenceLabel(settings.reminder_schedule);
  return [
    {
      icon: Mail,
      text: to || "No recipient yet",
      tone: to && isEmail(to) ? "default" : "danger"
    },
    {
      icon: CalendarClock,
      text: `Link lasts ${settings.expires_in_days} days`
    },
    {
      icon: Bell,
      text: reminders === "Off" ? "No reminders" : `Reminders: ${reminders}`,
      tone: reminders === "Off" ? "muted" : "default"
    },
    {
      icon: Video,
      text: video.upload
        ? `Uploading video, ${Math.round(video.upload.progress * 100)}%`
        : video.url
        ? "Welcome video"
        : "No welcome video",
      tone: video.url || video.upload ? "default" : "muted"
    },
    {
      icon: Palette,
      text: draft.enableFirmBranding ? "Firm branding" : "CaseBlink branding",
      tone: "muted"
    }
  ];
};

/** The delivery summary in one line, when Casey takes the rail's place. */
const DeliveryBar: FC<{ facts: Fact[]; onEdit: () => void }> = ({
  facts,
  onEdit
}) => (
  <div
    className="flex h-9 shrink-0 items-center gap-4 overflow-hidden border-b border-line-1 bg-fill-1 px-4"
    data-testid="delivery-bar"
  >
    {facts.slice(0, 4).map((f) => (
      <span
        key={f.text}
        className={cn(
          "flex min-w-0 items-center gap-1.5 text-sm",
          f.tone === "danger"
            ? "text-danger"
            : f.tone === "muted"
            ? "text-fg-3"
            : "text-fg-2"
        )}
      >
        <f.icon className="size-3.5 shrink-0" />
        <span className="truncate">{f.text}</span>
      </span>
    ))}
    <span className="flex-1" />
    <Button variant="ghost" size="xs" onClick={onEdit}>
      Edit Delivery
    </Button>
  </div>
);

/**
 * A request's page. A draft is the builder: the page bar (crumbs, status,
 * save state, Preview, Send…), one column with the title, the note to the
 * client and the questions, and a rail with delivery, sections and the
 * pre-send checks (a one-line delivery bar while Casey is open). Edits
 * autosave. Once sent it's the tracking page.
 */
export const IntakePage: FC<Props> = ({
  intakeId,
  clientEmail,
  clientName,
  preview = false
}) => {
  const { clientId, open, openList, setSelectedNodeIds } = useRequestsIde();
  const data = useRequestsData();
  const draft = useIntakeDraft(intakeId, clientEmail);
  const detail = data.detail(intakeId);
  const selection = useBuilderSelection(draft.schema);
  const library = useRequestsLibrary();
  const actions = useIntakeActions();
  const caseyOpen = useAgentPanelStore((s) => s.open);
  const building = useIsBuilding(intakeId);
  const clientFirstName = firstName(clientName ?? "") ?? "";
  const video = useIntakeWelcomeVideo(draft, clientFirstName);
  const [sendOpen, setSendOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [titling, setTitling] = useState(false);
  const freshIds = useRef(new Set<string>());
  const lastIds = useRef<Set<string> | null>(null);

  const { intake } = draft;
  const readOnly = !draft.editable;
  const { openId, jumpTo } = selection;

  useEffect(() => {
    if (readOnly && !detail) void data.loadDetail(intakeId);
  }, [data, detail, intakeId, readOnly]);

  // Casey reads the open question as the attorney's selection.
  useEffect(() => {
    setSelectedNodeIds(openId ? [openId] : []);
  }, [openId, setSelectedNodeIds]);

  // Steps the agent (or a reload) added since the page last saw the schema get an accent bar.
  useEffect(() => {
    const ids = new Set(stepsOf(draft.schema).map((s) => s.node.node_id));
    if (lastIds.current && draft.saveState === "saved") {
      const fresh = new Set<string>();
      ids.forEach((id) => {
        if (!lastIds.current!.has(id)) fresh.add(id);
      });
      if (fresh.size) freshIds.current = fresh;
    }
    lastIds.current = ids;
  }, [draft.schema, draft.saveState]);

  const setSchema = useCallback(
    (next: OutgoingIntakeFormNode[]) => {
      freshIds.current = new Set();
      draft.setSchema(next);
    },
    [draft]
  );

  const addForm = useCallback(
    async (templateId: string) => {
      const nodes = await data.loadTemplateSchema(templateId);
      const { schema, added } = addNodes(draft.schema, nodes);
      setSchema(schema);
      toast.success(
        added
          ? `Added ${added} question${added === 1 ? "" : "s"}`
          : "Every question was already in the draft"
      );
    },
    [data, draft.schema, setSchema]
  );

  const generateTitle = useCallback(async () => {
    setTitling(true);
    try {
      const title = await OutgoingIntakesAPI.generateTitle(
        clientId,
        draft.schema
      );
      if (title) draft.setTitle(title);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't suggest a title");
    } finally {
      setTitling(false);
    }
  }, [clientId, draft]);

  const applyText = (issue: OutgoingIntakeLintIssue, text: string) => {
    if (issue.key === "title") {
      if (text.trim()) draft.setTitle(text.trim());
      return;
    }
    if (issue.field === "note") {
      draft.setSettings({ attorney_note: text });
      return;
    }
    const node = issue.node_id ? findNode(draft.schema, issue.node_id) : null;
    if (!node) return;
    if (issue.field === "opt") {
      const optionId = issue.key.split(".opt.")[1];
      setSchema(
        updateNode(draft.schema, node.node_id, {
          options: (node.options ?? []).map((o) =>
            o.option_id === optionId ? { ...o, label: text } : o
          )
        })
      );
    } else if (issue.field === "help") {
      setSchema(
        updateNode(draft.schema, node.node_id, { help_text: text || null })
      );
    } else if (issue.field === "prompt" || issue.field === "title") {
      setSchema(
        updateNode(draft.schema, node.node_id, { [issue.field]: text })
      );
    }
  };

  const estimate = useMemo(() => intakeEstimate(draft.schema), [draft.schema]);
  const conditional = useMemo(
    () =>
      stepsOf(draft.schema).filter((s) => s.node.show_if?.question_id).length,
    [draft.schema]
  );
  const checks = useMemo(
    () =>
      readOnly
        ? []
        : presendChecklist(draft.schema, draft.settings.recipient_email),
    [draft.schema, draft.settings.recipient_email, readOnly]
  );
  const errors = errorCount(checks);
  const facts = deliveryFacts(draft, video);

  if (!intake) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-base text-fg-3">
        {draft.loading ? (
          <Spinner className="size-4" />
        ) : (
          <>
            This request no longer exists.
            <Button variant="secondary" size="sm" onClick={() => openList()}>
              Back to Intake Requests
            </Button>
          </>
        )}
      </div>
    );
  }

  const closePreview = () => open(intakeArtifact(intake), { replace: true });

  if (readOnly)
    return (
      <TrackPage
        intake={intake}
        schema={draft.schema}
        submissions={detail?.submissions ?? []}
        clientEmail={clientEmail}
        clientName={clientName ?? ""}
        preview={preview}
        onClosePreview={closePreview}
      />
    );

  const firstFix = () => {
    const item = checks.find((c) => c.severity === "error");
    if (item?.nodeId) jumpTo(item.nodeId);
    else setDeliveryOpen(true);
  };
  const steps = estimate.questions;
  const who = clientFirstName || "The client";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar
        crumbs={[{ label: "Intake Requests", onClick: () => openList() }]}
        title={draft.title || "Untitled Request"}
        meta={
          <>
            <StatusBadge>Draft</StatusBadge>
            <DraftSaveState
              state={draft.saveState}
              onRetry={() => void draft.flush()}
            />
          </>
        }
        actions={
          <>
            {errors > 0 && steps > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-danger"
                onClick={firstFix}
              >
                <AlertTriangle /> {errors} to Fix
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => open(previewArtifact(intake))}
            >
              <Eye /> Preview
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="More">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem
                  onSelect={async () => {
                    await draft.flush();
                    void actions.saveAsTemplate({
                      ...intake,
                      title: draft.title,
                      schema: draft.schema
                    });
                  }}
                >
                  <LayoutTemplate /> Save as Firm Template
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    void actions.duplicate(
                      { ...intake, title: draft.title },
                      draft.schema
                    )
                  }
                >
                  <Copy /> Duplicate
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => setConfirmDelete(true)}
                >
                  <Trash2 /> Delete Draft
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="primary"
              size="sm"
              disabled={steps === 0 || building || errors > 0}
              onClick={() => setSendOpen(true)}
            >
              <Send /> Send…
            </Button>
          </>
        }
      />

      {building ? (
        <div
          role="status"
          className="flex items-center gap-2 border-b border-line-1 bg-accent-soft px-4 py-2 text-sm text-fg"
        >
          <Loader2 className="size-3.5 shrink-0 animate-spin text-accent" />
          <span className="min-w-0 flex-1">
            {ASSISTANT_NAME} is building this draft. Its questions appear here
            when it&apos;s done. Editing is paused until then.
          </span>
        </div>
      ) : null}
      {draft.staleFromAgent ? (
        <div className="flex items-center gap-2 border-b border-line-1 bg-warn-soft px-4 py-2 text-sm text-warn">
          <AlertTriangle className="size-3.5" />
          <span className="min-w-0 flex-1">
            {ASSISTANT_NAME} changed this request while you had unsaved edits.
          </span>
          <Button variant="link" size="xs" onClick={() => void draft.reload()}>
            Reload to See It
          </Button>
        </div>
      ) : null}
      {caseyOpen ? (
        <DeliveryBar facts={facts} onEdit={() => setDeliveryOpen(true)} />
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex max-w-180 flex-col gap-5 px-6 py-6">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <input
                  value={draft.title}
                  onChange={(e) => draft.setTitle(e.target.value)}
                  placeholder="Untitled request"
                  aria-label="Request Title"
                  disabled={building}
                  className="min-w-0 flex-1 bg-transparent text-xl font-medium text-fg outline-none placeholder:text-fg-4"
                />
                <Button
                  variant="ghost"
                  size="xs"
                  disabled={titling || steps === 0 || building}
                  onClick={() => void generateTitle()}
                >
                  {titling ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Sparkles />
                  )}
                  Suggest Title
                </Button>
              </div>
              <span className="text-sm text-fg-3">
                {[
                  estimateLine(estimate),
                  conditional ? `${conditional} conditional` : null
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor={NOTE_ID} className="text-sm text-fg-3">
                Note to {clientFirstName || "the client"}
              </label>
              <Textarea
                id={NOTE_ID}
                value={draft.settings.attorney_note}
                rows={2}
                disabled={building}
                placeholder="A line or two at the top of the form: why you're asking and how long it takes."
                onChange={(e) =>
                  draft.setSettings({ attorney_note: e.target.value })
                }
                className="min-h-0"
              />
            </div>

            <QuestionsCard
              schema={draft.schema}
              onChange={setSchema}
              selection={selection}
              who={who}
              readOnly={building}
              freshIds={freshIds.current}
              renderLibrary={(section) => (
                <LibraryPicker
                  source={library}
                  onAddQuestion={(node) => {
                    const next = insertInSection(draft.schema, section, node);
                    if (!next.added) {
                      toast.message("That question is already in the request");
                      return;
                    }
                    setSchema(next.schema);
                    selection.setOpenId(node.node_id);
                  }}
                  onAddForm={(id) => void addForm(id)}
                />
              )}
              emptyHint={`Add a section to start, or ask ${ASSISTANT_NAME} to draft it.`}
            />
          </div>
        </div>

        {caseyOpen ? null : (
          <aside
            className="flex w-58 shrink-0 flex-col gap-6 overflow-y-auto border-l border-line-1 px-4 py-5"
            aria-label="Request Details"
          >
            <RailSection
              title="Delivery"
              action={
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setDeliveryOpen(true)}
                >
                  Edit
                </Button>
              }
            >
              <div className="flex flex-col gap-1.5">
                {facts.map((f) => (
                  <RailLine key={f.text} icon={f.icon} tone={f.tone}>
                    {f.text}
                  </RailLine>
                ))}
              </div>
            </RailSection>
            <RailSection title="Sections">
              <SectionJumpList schema={draft.schema} selection={selection} />
            </RailSection>
            <RailSection title="Before You Send">
              <ChecksList items={checks} onJump={jumpTo} compact />
            </RailSection>
          </aside>
        )}
      </div>

      <DeliverySheet
        open={deliveryOpen}
        onOpenChange={setDeliveryOpen}
        draft={draft}
        video={video}
        clientEmail={clientEmail}
        clientFirstName={clientFirstName}
      />
      <RecordWelcomeVideoDialog
        open={video.recording}
        onOpenChange={video.setRecording}
        clientFirstName={clientFirstName}
        title={draft.title}
        note={draft.settings.attorney_note}
        talkingPoints={video.talkingPoints}
        onUse={video.uploadFile}
        onUploadInstead={() => setDeliveryOpen(true)}
        onAskCasey={video.askCasey}
      />
      <LibraryDialog
        open={video.library}
        onOpenChange={video.setLibrary}
        currentId={video.videoId}
        onPick={(picked) => video.pickLibraryVideo(picked.video_id)}
      />
      <SendSheet
        intake={sendOpen ? intake : null}
        schema={draft.schema}
        delivery={draft.settings}
        clientEmail={clientEmail}
        clientName={clientName ?? undefined}
        onOpenChange={setSendOpen}
        beforeSend={draft.flush}
        onFix={jumpTo}
        onApplyText={applyText}
        onEditDelivery={() => setDeliveryOpen(true)}
        onEditNote={() => document.getElementById(NOTE_ID)?.focus()}
      />
      <PreviewDialog
        open={preview}
        onOpenChange={(o) => (o ? undefined : closePreview())}
        intake={intake}
        schema={draft.schema}
        clientName={clientName}
      />
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete “{draft.title || "this draft"}”?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The draft and its questions are gone for good. Nothing was sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep It</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={async () => {
                setConfirmDelete(false);
                if (await actions.remove(intake)) openList({ replace: true });
              }}
            >
              Delete Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
